import {Icon} from '@/app/components/Icon';
import {useContext, useState} from 'react';
import {Button} from '@/components/app/button';
import {Modal} from '@/components/app/modal';
import {StatusBadge} from '@/components/app/feedback';
import {cn} from '@/lib/utils';
import {useGetBiobookCountsQuery} from '../../../app/services/biobookApi';
import {
  useCheckBiotwinCalculationQuery,
  useGetBiotwinConditionsQuery,
  useGetBiotwinQuery,
  useGetBiotwinScoresQuery,
} from '../../../app/services/biotwinApi';
import {BiotwinSettingsForm} from '../../biotwin-settings/components/BiotwinSettingsForm';
import {useAppDispatch} from '../../../app/hooks/useAppDispatch';
import {useAppSelector} from '../../../app/hooks/useAppSelector';
import {
  deselectCondition,
  selectAllPerioperativeVisibleConditionIds,
  selectCondition,
  selectSelectedConditionIds,
  selectSelectedFilter,
  setAllPerioperativeVisibleConditionIds,
  setSelectedConditionIds,
  setSelectedFilter,
} from '../../person/personSlice';
import {BiotwinShareForm} from '../../biotwin-share/components/BiotwinShareForm';
import {AdaptiveCheckbox} from '../../../app/components/AdaptiveCheckbox';
import {useAssistantUtils} from '../../core/hooks/useAssistantUtils';
import {confirmDialogAsync} from '../../../app/confirmDialogAsync';
import {useGetBiotwinSettingsQuery} from '../../../app/services/biotwinSettingsApi';
import {PersonContext} from '../../person/contexts/PersonContext';
import {
  getConditionTypeColorByName,
  getRequiredRelatedRelevantConditions,
  getSelectedRelevantConditions,
} from '../../biotwin/utils';
import {BiotwinScore, RelevantCondition} from '../../biotwin/types';
import {BiotwinBasicsViewer} from '../../biotwin/components/BiotwinBasicsViewer';
import {BiotwinHistory} from '../../biotwin/components/BiotwinHistory';
import {BiotwinHubUsers} from '../../biotwin/components/BiotwinHubUsers';
import {BiotwinInfo} from '../../biotwin/components/BiotwinInfo';
import {ManageDemo} from '../../biotwin/components/ManageDemo';
import {ManageSample} from '../../biotwin/components/ManageSample';
import {ScoreInfo} from '../../biotwin/components/ScoreInfo';
import {isCompleteBiotwin, isInPerioperative, isPerioperativeSetting, isProcedure} from '../../biotwin/helpers/utils';
import {BiobookFilter} from '../types';
import {useBiobookPages} from '../hooks/useBiobookPages';
export interface BiobookFilterPanelProps {
  className?: string;
  selection?: boolean;
}
export const BiobookFilterPanel = ({className, selection}: BiobookFilterPanelProps) => {
  const {relevantConditions, userId} = useContext(PersonContext);
  const dispatch = useAppDispatch();
  const allPerioperativeVisibleConditionIds = useAppSelector(selectAllPerioperativeVisibleConditionIds);
  const selectedConditionIds = useAppSelector(selectSelectedConditionIds);
  const selectedFilter = useAppSelector(selectSelectedFilter);
  const {isLoading: isLoadingCheckBiotwinCalculation} = useCheckBiotwinCalculationQuery({
    id: userId,
  });
  const {
    biobook,
    bookmarkedBiobookPages,
    drugMultiIndexedBiobookPages,
    multiIndexedBiobookPages,
    neoplasticBiobookPages,
    standardAndPolicyBiobookPages,
    updatedBiobookPages,
    matchedBiobookPages,
  } = useBiobookPages();
  const {data: biotwin, isLoading: isLoadingGetBiotwin} = useGetBiotwinQuery({
    id: userId,
  });
  const {data: biotwinScores} = useGetBiotwinScoresQuery(
    {
      userId,
    },
    {
      skip: isLoadingCheckBiotwinCalculation || !isCompleteBiotwin(biotwin),
    },
  );
  const {data: biobookCounts} = useGetBiobookCountsQuery(
    {
      userId,
    },
    {
      skip: isLoadingCheckBiotwinCalculation || !isCompleteBiotwin(biotwin),
    },
  );
  const {data: biotwinConditions} = useGetBiotwinConditionsQuery(
    {
      userId,
    },
    {
      skip: isLoadingCheckBiotwinCalculation,
    },
  );
  const {data: biotwinSettings} = useGetBiotwinSettingsQuery(
    {
      userId,
    },
    {
      skip: isLoadingCheckBiotwinCalculation,
    },
  );
  const selectedRelevantConditions = getSelectedRelevantConditions(relevantConditions ?? []);
  const requiredRelatedRelevantConditions = getRequiredRelatedRelevantConditions(relevantConditions ?? []);
  const visibleRequiredRelatedRelevantConditions = requiredRelatedRelevantConditions.filter((relevantCondition) => {
    const phraseCount =
      biobookCounts?.conditionPhraseCounts.find(({conditionId}) => conditionId === relevantCondition.conditionId)
        ?.phraseCount ?? 0;
    if (selection) {
      return phraseCount > 0;
    } else {
      return !relevantCondition.hideFromAssigned && (phraseCount > 0 || !relevantCondition.hideWhenNoPhrase);
    }
  });
  const [settingsDialogVisible, setSettingsDialogVisible] = useState(false);
  const [scoreDialogState, setScoreDialogState] = useState<
    | {
        visible: false;
      }
    | {
        biotwinScore: BiotwinScore;
        visible: true;
      }
  >({
    visible: false,
  });
  const [demoDialogVisible, setDemoDialogVisible] = useState(false);
  const [shareDialogVisible, setShareDialogVisible] = useState(false);
  const [sampleDialogVisible, setSampleDialogVisible] = useState(false);
  const {pushAssistantContent, removeAssistantContent} = useAssistantUtils();
  const [historyDialogVisible, setHistoryDialogVisible] = useState(false);
  const [infoDialogVisible, setInfoDialogVisible] = useState(false);
  const index = selection
    ? selectedRelevantConditions.filter((relevantCondition) =>
        biobook.some((biobookPage) => biobookPage.matchedConditionIds.includes(relevantCondition.conditionId)),
      )
    : selectedRelevantConditions.filter((relevantCondition) =>
        biobookCounts?.conditionPhraseCounts.some(({conditionId}) => conditionId === relevantCondition.conditionId),
      );
  const filtersSectionVisible =
    (updatedBiobookPages.length > 0 ||
      bookmarkedBiobookPages.length > 0 ||
      multiIndexedBiobookPages.length > 0 ||
      drugMultiIndexedBiobookPages.length > 0 ||
      neoplasticBiobookPages.length > 0 ||
      (biotwinSettings?.policyAndStandardPhrasesVisible && standardAndPolicyBiobookPages.length > 0)) &&
    matchedBiobookPages.length > 0;
  return (
    <>
      <div className={cn(className, 'flex flex-col min-h-0')}>
        <div className="pt-4 px-4 bg-white sm:pt-6 sm:pb-2 sm:px-6">
          {biotwin?.isDemo && (
            <StatusBadge className="font-normal mb-2 w-full" tone="warning">
              {'Demo'}
            </StatusBadge>
          )}
          {biotwin?.isSample && (
            <StatusBadge className="font-normal mb-2 w-full" tone="success">
              {'Sample'}
            </StatusBadge>
          )}
          <BiotwinHubUsers biotwinUserId={userId} />
          <BiotwinBasicsViewer
            className="grow shrink w-full flex flex-wrap items-center gap-2 whitespace-nowrap"
            isLoading={isLoadingGetBiotwin}
            short
            value={biotwin}
          />
        </div>
        <div className="flex-1 pt-2 pb-4 px-4 overflow-auto bg-white sm:pt-2 sm:px-6 sm:pb-6">
          <div className="flex flex-wrap items-center gap-2 whitespace-nowrap">
            {selection && filtersSectionVisible && (
              <section className={'flex flex-col gap-2 w-full'} data-name="user-biotwin-summary-filters">
                <h6 className="mt-2 mb-1 text-muted-foreground text-base">Filters</h6>
                {updatedBiobookPages.length > 0 && (
                  <AdaptiveCheckbox
                    checked={selectedFilter === 'updates'}
                    disabled={isFilterDisabled('updates')}
                    label={`Updates (${updatedBiobookPages.length})`}
                    onChange={() => toggleFilter('updates')}
                    radio
                  />
                )}
                {bookmarkedBiobookPages.length > 0 && (
                  <AdaptiveCheckbox
                    checked={selectedFilter === 'favorites'}
                    disabled={isFilterDisabled('favorites')}
                    label={`Bookmarks (${bookmarkedBiobookPages.length})`}
                    onChange={() => toggleFilter('favorites')}
                    radio
                  />
                )}
                {multiIndexedBiobookPages.length > 0 && (
                  <AdaptiveCheckbox
                    checked={selectedFilter === 'multiIndexed'}
                    disabled={isFilterDisabled('multiIndexed')}
                    label={`Multi-indexed (${multiIndexedBiobookPages.length})`}
                    onChange={() => toggleFilter('multiIndexed')}
                    radio
                  />
                )}
                {drugMultiIndexedBiobookPages.length > 0 && (
                  <AdaptiveCheckbox
                    checked={selectedFilter === 'drugMultiIndexed'}
                    disabled={isFilterDisabled('drugMultiIndexed')}
                    info="Please note: The information provided on these pages consists of content from this Biobook related to drug combinations. It is not an exhaustive list of all relevant drug-drug interactions."
                    label={`Drug Multi-indexed (${drugMultiIndexedBiobookPages.length})`}
                    onChange={() => toggleFilter('drugMultiIndexed')}
                    radio
                  />
                )}
                {neoplasticBiobookPages.length > 0 && (
                  <AdaptiveCheckbox
                    checked={selectedFilter === 'neoplastic'}
                    disabled={isFilterDisabled('neoplastic')}
                    label={`Neoplastic (${neoplasticBiobookPages.length})`}
                    onChange={() => toggleFilter('neoplastic')}
                    radio
                  />
                )}
                {biotwinSettings?.policyAndStandardPhrasesVisible && standardAndPolicyBiobookPages.length > 0 && (
                  <AdaptiveCheckbox
                    checked={selectedFilter === 'standardAndPolicy'}
                    disabled={isFilterDisabled('standardAndPolicy')}
                    label={`Standard & Policy (${standardAndPolicyBiobookPages.length})`}
                    onChange={() => toggleFilter('standardAndPolicy')}
                    radio
                  />
                )}
              </section>
            )}
            {((selection && index.length > 0) || visibleRequiredRelatedRelevantConditions.length > 0) && (
              <section
                className={'flex flex-col gap-2 w-full'}
                data-name="user-biotwin-summary-index"
                onMouseEnter={() => {
                  pushAssistantContent({
                    detail: 'Please select one or more features to review only specific pages in a shortened biobook.',
                    id: 'user-biotwin-summary-index',
                    summary: 'Feature-specific information',
                  });
                }}
                onMouseLeave={() => {
                  removeAssistantContent('user-biotwin-summary-index');
                }}
              >
                {selection && index.length > 0 && (
                  <>
                    <h6 className="mt-2 mb-1 text-muted-foreground text-base">Index</h6>
                    {renderRelevantConditionListItems(index)}
                  </>
                )}
                {visibleRequiredRelatedRelevantConditions.length > 0 && (
                  <>
                    <h6 className="mt-2 mb-1 text-muted-foreground text-base">Assigned</h6>
                    {renderRelevantConditionListItems(visibleRequiredRelatedRelevantConditions)}
                  </>
                )}
              </section>
            )}
            {selectedConditionIds?.length > 0 && (
              <Button
                className={cn('text-muted-foreground w-full p-2 underline', 'rounded-full')}
                onClick={() => dispatch(setSelectedConditionIds([]))}
                size="sm"
                variant="ghost"
                type="button"
              >
                {'Reset'}
              </Button>
            )}
            {!!biotwinScores?.length && (
              <section className="flex flex-wrap items-center gap-2 whitespace-nowrap w-full">
                <h6 className="mt-2 mb-1 text-base text-muted-foreground">Scores</h6>
                {biotwinScores.map((biotwinScore) => (
                  <div
                    className="flex items-center gap-2 min-w-0 w-full"
                    key={biotwinScore.scoreId}
                    onClick={() =>
                      setScoreDialogState({
                        biotwinScore,
                        visible: true,
                      })
                    }
                  >
                    <span className="flex-1 overflow-hidden whitespace-nowrap text-ellipsis min-w-0 text-xs">
                      <span className="text-indigo-400">{biotwinScore.scoreShortDescription}</span>{' '}
                      {biotwinScore.scoreValue} {biotwinScore.scoreValueUnit}{' '}
                      {biotwinScore.hasMissingInformation && (
                        <span className="text-yellow-600">
                          <Icon icon="exclamation-triangle" className="text-xs" /> Missing Information
                        </span>
                      )}
                    </span>
                  </div>
                ))}
              </section>
            )}
          </div>
        </div>
      </div>

      <Modal title="Biobook Settings" onClose={() => setSettingsDialogVisible(false)} open={settingsDialogVisible}>
        <BiotwinSettingsForm userId={userId} />
      </Modal>

      <Modal
        title={scoreDialogState.visible ? scoreDialogState.biotwinScore.scoreShortDescription : 'Score'}
        onClose={() =>
          setScoreDialogState({
            visible: false,
          })
        }
        open={scoreDialogState.visible}
      >
        {scoreDialogState.visible && <ScoreInfo biotwinScore={scoreDialogState.biotwinScore} className="w-[30rem]" />}
      </Modal>

      <Modal
        className={'w-[30rem]'}
        title={`Share ${biotwin?.name ?? 'Biotwin'}`}
        onClose={() => setShareDialogVisible(false)}
        open={shareDialogVisible}
      >
        {biotwin != null && (
          <BiotwinShareForm biotwinId={biotwin.id} onSubmitSuccess={() => setShareDialogVisible(false)} />
        )}
      </Modal>

      <Modal
        className={'h-full'}
        title={`${biotwin?.name}'s Timeline`}
        onClose={() => setHistoryDialogVisible(false)}
        style={{
          maxWidth: '60rem',
          width: 'calc(100% - 3rem)',
        }}
        open={historyDialogVisible}
      >
        {biotwin && <BiotwinHistory userId={biotwin.id} />}
      </Modal>

      <Modal
        className={'w-[30rem]'}
        title={'Biotwin Info'}
        onClose={() => setInfoDialogVisible(false)}
        open={infoDialogVisible}
      >
        {biotwin && <BiotwinInfo biotwin={biotwin} />}
      </Modal>

      <Modal
        className={'w-[30rem]'}
        title={`Sample Settings`}
        onClose={() => setSampleDialogVisible(false)}
        open={sampleDialogVisible}
      >
        <ManageSample userId={userId} />
      </Modal>

      <Modal
        className={'w-[30rem]'}
        title={`Demo Settings`}
        onClose={() => setDemoDialogVisible(false)}
        open={demoDialogVisible}
      >
        <ManageDemo userId={userId} />
      </Modal>
    </>
  );
  function toggleFilter(key: BiobookFilter) {
    dispatch(setSelectedFilter(selectedFilter === key ? null : key));
  }
  function getBiobookPagesOfFilter(key: BiobookFilter | null) {
    if (key === 'updates') {
      return updatedBiobookPages;
    }
    if (key === 'favorites') {
      return bookmarkedBiobookPages;
    }
    if (key === 'multiIndexed') {
      return multiIndexedBiobookPages;
    }
    if (key === 'drugMultiIndexed') {
      return drugMultiIndexedBiobookPages;
    }
    if (key === 'neoplastic') {
      return neoplasticBiobookPages;
    }
    if (key === 'standardAndPolicy') {
      return standardAndPolicyBiobookPages;
    }
    return biobook;
  }
  function isFilterDisabled(key: BiobookFilter) {
    const biobookPagesOfFilter = getBiobookPagesOfFilter(key);
    if (selectedConditionIds.length > 0) {
      return !matchedBiobookPages.some((biobookPage) => biobookPagesOfFilter.includes(biobookPage));
    }
    return biobookPagesOfFilter.length === 0;
  }
  function renderRelevantConditionListItems(relevantConditions: RelevantCondition[]) {
    return relevantConditions.map((relevantCondition) => {
      const empty = selection
        ? matchedBiobookPages.every(
            (biobookPage) => !biobookPage.matchedConditionIds.includes(relevantCondition.conditionId),
          )
        : false;
      const selected = selectedConditionIds.includes(relevantCondition.conditionId);
      return (
        <AdaptiveCheckbox
          checked={selected}
          color={getConditionTypeColorByName(relevantCondition.conditionTypeName.trim())}
          disabled={selection && empty}
          key={relevantCondition.conditionId}
          label={relevantCondition.conditionName}
          onChange={async ({checked}) => {
            if (checked) {
              const result = await askAllPerioperativeVisible(relevantCondition);
              if (result !== false) {
                dispatch(selectCondition(relevantCondition.conditionId));
              }
            } else {
              dispatch(deselectCondition(relevantCondition.conditionId));
            }
          }}
          readOnly={!selection}
        />
      );
    });
  }
  async function askAllPerioperativeVisible(rc: RelevantCondition): Promise<boolean | undefined> {
    if (isPerioperativeSetting(rc)) {
      if (!selectedConditionIds.some((id) => shouldTriggerPerioperativeConfirmation(id))) {
        return;
      }
    } else {
      if (!shouldTriggerPerioperativeConfirmation(rc.conditionId)) {
        return;
      }
      const perioperativeSetting = relevantConditions?.find((r) => isPerioperativeSetting(r));
      if (!selectedConditionIds.includes(perioperativeSetting?.conditionId ?? -1)) {
        return;
      }
    }
    const confirmed = await confirmDialogAsync({
      confirmClassName: 'rounded-full',
      className: 'w-[30rem]',
      title: 'Perioperative Notice',
      message: `It has been over 90 days since the "${rc.conditionName}" date. Therefore, pages specific to the "Perioperative setting" and "${rc.conditionName}" are currently masked from the biotwin. Would you like to view these pages again for this session?`,
      cancelClassName: 'rounded-full bg-transparent text-muted-foreground',
    });
    if (!confirmed) {
      dispatch(deselectCondition(rc.conditionId));
      return false;
    }
    if (confirmed) {
      dispatch(setAllPerioperativeVisibleConditionIds([...allPerioperativeVisibleConditionIds, rc.conditionId]));
    }
  }
  function shouldTriggerPerioperativeConfirmation(conditionId: number) {
    const rc = relevantConditions?.find((rc) => rc.conditionId === conditionId);
    if (rc == null) {
      return false;
    }

    // Only procedures
    if (!isProcedure(rc)) {
      return false;
    }

    // Check if the condition's biotwin condition is perioperative
    const biotwinCondition = biotwinConditions?.find(({conditionId}) => conditionId === rc.conditionId);
    if (biotwinCondition == null || isInPerioperative(biotwinCondition)) {
      return false;
    }

    // Check if all perioperative pages are already visible
    const allPerioperativeVisible = allPerioperativeVisibleConditionIds.includes(rc.conditionId);
    if (allPerioperativeVisible) {
      return false;
    }

    // Check if there are any perioperative biobook pages for this condition
    const biobookPages = biobook.filter((biobookPage) => biobookPage.matchedConditionIds.includes(rc.conditionId));
    const hasPerioperativeBiobookPages = biobookPages.some((biobookPage) =>
      biobookPage.matchedConditionNames.some((conditionName) => isPerioperativeSetting(conditionName)),
    );
    if (!hasPerioperativeBiobookPages) {
      return false;
    }
    return true;
  }
};
